import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  UserPlus, 
  Edit, 
  Trash2, 
  Search, 
  Filter, 
  Crown, 
  CreditCard, 
  Mail, 
  Calendar,
  Settings,
  AlertTriangle,
  Check,
  X,
  RefreshCw,
  Download,
  Upload,
  Shield,
  Users,
  BarChart3,
  Zap,
  UserCheck,
  UserX,
  Loader2,
  Eye,
  Star,
  TrendingUp,
  Award
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import AdminNavigation from '@/components/AdminNavigation';
import { LoadingState } from '@/components/LoadingState';

interface User {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  nickname: string | null;
  gender: string | null;
  profilePictureUrl: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  subscriptionStatus: string;
  subscriptionSource: string | null;
  trialEndsAt: string | null;
  subscriptionEndsAt: string | null;
  pendingPromoCode: string | null;
  appliedPromoCode: string | null;
  isAdmin: boolean;
  isOfficialBearInsider: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateUserData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  nickname?: string;
  gender?: string;
  subscriptionStatus?: string;
  isAdmin?: boolean;
  isOfficialBearInsider?: boolean;
}

interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  nickname?: string;
  gender?: string;
  subscriptionStatus?: string;
  subscriptionSource?: string;
  pendingPromoCode?: string;
  appliedPromoCode?: string;
  isAdmin?: boolean;
  isOfficialBearInsider?: boolean;
}

const UserStatusBadge = ({ status }: { status: string }) => {
  const getVariant = () => {
    switch (status.toLowerCase()) {
      case 'lifetime':
        return 'default';
      case 'active':
        return 'default';
      case 'trial':
        return 'secondary';
      case 'canceled':
      case 'past_due':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  return (
    <Badge variant={getVariant()}>
      {status === 'lifetime' && <Crown className="w-3 h-3 mr-1" />}
      {status}
    </Badge>
  );
};

export default function AdminUserManagement() {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showBulkActionDialog, setShowBulkActionDialog] = useState(false);
  const [showAnalyticsDialog, setShowAnalyticsDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState<'activate' | 'deactivate' | 'delete' | 'export'>('activate');
  
  // Form states
  const [createForm, setCreateForm] = useState<CreateUserData>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    nickname: '',
    gender: '',
    subscriptionStatus: 'trial',
    isAdmin: false,
    isOfficialBearInsider: false,
  });
  
  const [updateForm, setUpdateForm] = useState<UpdateUserData>({});

  // Get users with pagination
  const { data: usersData, isLoading, refetch } = useQuery({
    queryKey: ['/api/admin/users', searchTerm, statusFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('limit', '50');
      return fetch(`/api/admin/users?${params}`).then(res => res.json());
    },
    select: (response) => response.data as User[],
  });

  // Get user analytics
  const { data: userAnalytics } = useQuery({
    queryKey: ['/api/admin/users/analytics'],
    queryFn: () => fetch('/api/admin/users/analytics').then(res => res.json()),
    select: (response) => response.data,
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: (data: CreateUserData) =>
      fetch('/api/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }).then(res => res.json()),
    onSuccess: () => {
      toast({
        title: "User created",
        description: "New user has been created successfully",
      });
      setShowCreateDialog(false);
      setCreateForm({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        nickname: '',
        gender: '',
        subscriptionStatus: 'trial',
        isAdmin: false,
        isOfficialBearInsider: false,
      });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Error creating user",
        description: error.message || "Failed to create user",
        variant: "destructive",
      });
    }
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserData }) =>
      fetch(`/api/admin/users/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }).then(res => res.json()),
    onSuccess: () => {
      toast({
        title: "User updated",
        description: "User has been updated successfully",
      });
      setShowEditDialog(false);
      setSelectedUser(null);
      setUpdateForm({});
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Error updating user",
        description: error.message || "Failed to update user",
        variant: "destructive",
      });
    }
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (id: number) =>
      fetch(`/api/admin/users/${id}`, {
        method: 'DELETE',
      }).then(res => res.json()),
    onSuccess: () => {
      toast({
        title: "User deleted",
        description: "User has been deleted successfully",
      });
      setShowDeleteDialog(false);
      setSelectedUser(null);
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Error deleting user",
        description: error.message || "Failed to delete user",
        variant: "destructive",
      });
    }
  });

  // Sync user with Stripe mutation
  const syncStripeUserMutation = useMutation({
    mutationFn: (userId: number) =>
      fetch(`/api/admin/users/${userId}/sync-stripe`, {
        method: 'POST',
      }).then(res => res.json()),
    onSuccess: () => {
      toast({
        title: "Stripe sync completed",
        description: "User has been synced with Stripe successfully",
      });
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Stripe sync failed",
        description: error.message || "Failed to sync user with Stripe",
        variant: "destructive",
      });
    }
  });

  // Bulk actions mutation
  const bulkActionMutation = useMutation({
    mutationFn: ({ action, userIds }: { action: string; userIds: number[] }) =>
      fetch('/api/admin/users/bulk-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, userIds }),
      }).then(res => res.json()),
    onSuccess: (_, variables) => {
      toast({
        title: "Bulk action completed",
        description: `Successfully ${variables.action}d ${variables.userIds.length} users`,
      });
      setShowBulkActionDialog(false);
      setSelectedUsers(new Set());
      refetch();
    },
    onError: (error: any) => {
      toast({
        title: "Bulk action failed",
        description: error.message || "Failed to perform bulk action",
        variant: "destructive",
      });
    }
  });

  const users = usersData || [];
  
  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || user.subscriptionStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setUpdateForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      nickname: user.nickname || '',
      gender: user.gender || '',
      subscriptionStatus: user.subscriptionStatus,
      subscriptionSource: user.subscriptionSource || '',
      pendingPromoCode: user.pendingPromoCode || '',
      appliedPromoCode: user.appliedPromoCode || '',
      isAdmin: user.isAdmin,
      isOfficialBearInsider: user.isOfficialBearInsider,
    });
    setShowEditDialog(true);
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  // Bulk operation handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleSelectUser = (userId: number, checked: boolean) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleBulkAction = () => {
    bulkActionMutation.mutate({
      action: bulkAction,
      userIds: Array.from(selectedUsers),
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AdminNavigation currentPage="User Management" />
      
      <div className="container mx-auto px-4 py-6">
        <div className="space-y-6">
          
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">User Management</h1>
              <p className="text-gray-600 dark:text-gray-400">Manage users, subscriptions, and access levels</p>
            </div>
            
            <div className="flex space-x-2">
              <Button onClick={() => refetch()} variant="outline" data-testid="button-refresh">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={() => setShowAnalyticsDialog(true)} variant="outline" data-testid="button-analytics">
                <BarChart3 className="h-4 w-4 mr-2" />
                Analytics
              </Button>
              <Button onClick={() => setShowCreateDialog(true)} data-testid="button-create-user">
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </div>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex space-x-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search users by email, name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="input-search"
                    />
                  </div>
                </div>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-48" data-testid="select-status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="lifetime">Lifetime</SelectItem>
                    <SelectItem value="canceled">Canceled</SelectItem>
                    <SelectItem value="past_due">Past Due</SelectItem>
                  </SelectContent>
                </Select>

                {/* Bulk Actions */}
                {selectedUsers.size > 0 && (
                  <Button 
                    onClick={() => setShowBulkActionDialog(true)}
                    data-testid="button-bulk-actions"
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    Actions ({selectedUsers.size})
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* User Analytics Summary */}
          {userAnalytics && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card data-testid="analytics-total-users">
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <Users className="h-8 w-8 text-blue-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Total Users</p>
                      <p className="text-2xl font-bold">{userAnalytics.totalUsers || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="analytics-active-users">
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <UserCheck className="h-8 w-8 text-green-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Active Users</p>
                      <p className="text-2xl font-bold">{userAnalytics.activeUsers || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="analytics-new-users">
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <TrendingUp className="h-8 w-8 text-purple-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">New This Month</p>
                      <p className="text-2xl font-bold">{userAnalytics.newUsersThisMonth || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="analytics-conversion-rate">
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <Star className="h-8 w-8 text-yellow-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                      <p className="text-2xl font-bold">{userAnalytics.conversionRate || '0%'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Users Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Users ({filteredUsers.length})</span>
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                  <Button variant="outline" size="sm">
                    <Upload className="h-4 w-4 mr-2" />
                    Import
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <LoadingState message="Loading users..." />
              ) : filteredUsers.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <input
                            type="checkbox"
                            checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            data-testid="checkbox-select-all"
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Subscription</TableHead>
                        <TableHead>Badges</TableHead>
                        <TableHead>Stripe</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((user) => (
                        <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedUsers.has(user.id)}
                              onChange={(e) => handleSelectUser(user.id, e.target.checked)}
                              data-testid={`checkbox-user-${user.id}`}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">
                                {user.firstName} {user.lastName}
                                {user.nickname && (
                                  <span className="text-gray-500 ml-2">({user.nickname})</span>
                                )}
                              </p>
                              <p className="text-sm text-gray-500">ID: {user.id}</p>
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Mail className="h-4 w-4 text-gray-400" />
                              <span>{user.email}</span>
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <div className="space-y-1">
                              <UserStatusBadge status={user.subscriptionStatus} />
                              {user.subscriptionSource && (
                                <p className="text-xs text-gray-500">
                                  Source: {user.subscriptionSource}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {user.isAdmin && (
                                <Badge variant="destructive" className="flex items-center gap-1">
                                  <Shield className="h-3 w-3" />
                                  Admin
                                </Badge>
                              )}
                              {user.isOfficialBearInsider && (
                                <Badge variant="default" className="flex items-center gap-1">
                                  <Crown className="h-3 w-3" />
                                  Bear Insider
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <div className="space-y-1">
                              {user.stripeCustomerId ? (
                                <div className="flex items-center space-x-1">
                                  <Check className="h-4 w-4 text-green-500" />
                                  <span className="text-sm">Synced</span>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-1">
                                  <X className="h-4 w-4 text-red-500" />
                                  <span className="text-sm">Not synced</span>
                                </div>
                              )}
                              {user.stripeCustomerId && (
                                <p className="text-xs text-gray-500 truncate">
                                  {user.stripeCustomerId}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <div className="flex items-center space-x-1">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="text-sm">
                                {new Date(user.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </TableCell>
                          
                          <TableCell>
                            <div className="flex space-x-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditUser(user)}
                                data-testid={`button-edit-${user.id}`}
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                              
                              {!user.stripeCustomerId && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => syncStripeUserMutation.mutate(user.id)}
                                  disabled={syncStripeUserMutation.isPending}
                                  data-testid={`button-sync-stripe-${user.id}`}
                                >
                                  <CreditCard className="h-3 w-3" />
                                </Button>
                              )}
                              
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteUser(user)}
                                className="text-red-600 hover:text-red-700"
                                data-testid={`button-delete-${user.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <UserPlus className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No users found</p>
                  <p className="text-sm">
                    {searchTerm || statusFilter !== 'all' 
                      ? 'Try adjusting your search criteria' 
                      : 'Start by adding your first user'
                    }
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Add a new user to the system
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={createForm.firstName}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, firstName: e.target.value }))}
                  data-testid="input-first-name"
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={createForm.lastName}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, lastName: e.target.value }))}
                  data-testid="input-last-name"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                data-testid="input-email"
              />
            </div>
            
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                data-testid="input-password"
              />
            </div>
            
            <div>
              <Label htmlFor="subscriptionStatus">Subscription Status</Label>
              <Select
                value={createForm.subscriptionStatus}
                onValueChange={(value) => setCreateForm(prev => ({ ...prev, subscriptionStatus: value }))}
              >
                <SelectTrigger data-testid="select-subscription-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="lifetime">Lifetime</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isAdmin"
                  checked={createForm.isAdmin}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, isAdmin: e.target.checked }))}
                  data-testid="checkbox-admin"
                />
                <Label htmlFor="isAdmin">Admin Access</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isOfficialBearInsider"
                  checked={createForm.isOfficialBearInsider}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, isOfficialBearInsider: e.target.checked }))}
                  data-testid="checkbox-bear-insider"
                />
                <Label htmlFor="isOfficialBearInsider">Official Bear Insider</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              data-testid="button-cancel-create"
            >
              Cancel
            </Button>
            <Button
              onClick={() => createUserMutation.mutate(createForm)}
              disabled={createUserMutation.isPending || !createForm.email || !createForm.password}
              data-testid="button-save-create"
            >
              {createUserMutation.isPending ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user information and settings
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-firstName">First Name</Label>
                <Input
                  id="edit-firstName"
                  value={updateForm.firstName || ''}
                  onChange={(e) => setUpdateForm(prev => ({ ...prev, firstName: e.target.value }))}
                  data-testid="input-edit-first-name"
                />
              </div>
              <div>
                <Label htmlFor="edit-lastName">Last Name</Label>
                <Input
                  id="edit-lastName"
                  value={updateForm.lastName || ''}
                  onChange={(e) => setUpdateForm(prev => ({ ...prev, lastName: e.target.value }))}
                  data-testid="input-edit-last-name"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="edit-subscriptionStatus">Subscription Status</Label>
              <Select
                value={updateForm.subscriptionStatus}
                onValueChange={(value) => setUpdateForm(prev => ({ ...prev, subscriptionStatus: value }))}
              >
                <SelectTrigger data-testid="select-edit-subscription-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="lifetime">Lifetime</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit-isAdmin"
                  checked={updateForm.isAdmin || false}
                  onChange={(e) => setUpdateForm(prev => ({ ...prev, isAdmin: e.target.checked }))}
                  data-testid="checkbox-edit-admin"
                />
                <Label htmlFor="edit-isAdmin">Admin Access</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit-isOfficialBearInsider"
                  checked={updateForm.isOfficialBearInsider || false}
                  onChange={(e) => setUpdateForm(prev => ({ ...prev, isOfficialBearInsider: e.target.checked }))}
                  data-testid="checkbox-edit-bear-insider"
                />
                <Label htmlFor="edit-isOfficialBearInsider">Official Bear Insider</Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              data-testid="button-cancel-edit"
            >
              Cancel
            </Button>
            <Button
              onClick={() => selectedUser && updateUserMutation.mutate({ id: selectedUser.id, data: updateForm })}
              disabled={updateUserMutation.isPending}
              data-testid="button-save-edit"
            >
              {updateUserMutation.isPending ? 'Updating...' : 'Update User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this user? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                You are about to delete <strong>{selectedUser.firstName} {selectedUser.lastName}</strong> ({selectedUser.email})
              </AlertDescription>
            </Alert>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              data-testid="button-cancel-delete"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedUser && deleteUserMutation.mutate(selectedUser.id)}
              disabled={deleteUserMutation.isPending}
              data-testid="button-confirm-delete"
            >
              {deleteUserMutation.isPending ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={showBulkActionDialog} onOpenChange={setShowBulkActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Actions</DialogTitle>
            <DialogDescription>
              Perform actions on {selectedUsers.size} selected users
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Action</Label>
              <Select value={bulkAction} onValueChange={(value: any) => setBulkAction(value)}>
                <SelectTrigger data-testid="select-bulk-action">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="activate">Activate Users</SelectItem>
                  <SelectItem value="deactivate">Deactivate Users</SelectItem>
                  <SelectItem value="export">Export User Data</SelectItem>
                  <SelectItem value="delete">Delete Users</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {bulkAction === 'delete' && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  This action cannot be undone. Selected users will be permanently deleted.
                </AlertDescription>
              </Alert>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBulkActionDialog(false)}
              data-testid="button-cancel-bulk-action"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkAction}
              disabled={bulkActionMutation.isPending}
              variant={bulkAction === 'delete' ? 'destructive' : 'default'}
              data-testid="button-confirm-bulk-action"
            >
              {bulkActionMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Zap className="h-4 w-4 mr-2" />
              )}
              {bulkAction === 'delete' ? 'Delete Users' : `${bulkAction} Users`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={showAnalyticsDialog} onOpenChange={setShowAnalyticsDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>User Analytics Dashboard</DialogTitle>
            <DialogDescription>
              Comprehensive user statistics and insights
            </DialogDescription>
          </DialogHeader>
          
          {userAnalytics ? (
            <div className="space-y-6">
              {/* Key Metrics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <Users className="h-6 w-6 mx-auto text-blue-600 mb-2" />
                      <p className="text-2xl font-bold">{userAnalytics.totalUsers || 0}</p>
                      <p className="text-sm text-gray-600">Total Users</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <UserCheck className="h-6 w-6 mx-auto text-green-600 mb-2" />
                      <p className="text-2xl font-bold">{userAnalytics.activeUsers || 0}</p>
                      <p className="text-sm text-gray-600">Active</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <Crown className="h-6 w-6 mx-auto text-yellow-600 mb-2" />
                      <p className="text-2xl font-bold">{userAnalytics.lifetimeUsers || 0}</p>
                      <p className="text-sm text-gray-600">Lifetime</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <TrendingUp className="h-6 w-6 mx-auto text-purple-600 mb-2" />
                      <p className="text-2xl font-bold">{userAnalytics.growthRate || '0%'}</p>
                      <p className="text-sm text-gray-600">Growth Rate</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Subscription Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Subscription Status Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {userAnalytics.subscriptionBreakdown?.map((item: any, index: number) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className={`w-3 h-3 rounded-full ${
                            item.status === 'active' ? 'bg-green-500' :
                            item.status === 'lifetime' ? 'bg-yellow-500' :
                            item.status === 'trial' ? 'bg-blue-500' : 'bg-gray-500'
                          }`} />
                          <span className="capitalize">{item.status}</span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="font-medium">{item.count}</span>
                          <span className="text-sm text-gray-500">({item.percentage}%)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent User Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>New registrations (7 days):</span>
                      <span className="font-medium">{userAnalytics.newUsersLast7Days || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>New registrations (30 days):</span>
                      <span className="font-medium">{userAnalytics.newUsersThisMonth || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Trial to paid conversion rate:</span>
                      <span className="font-medium">{userAnalytics.conversionRate || '0%'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Average engagement score:</span>
                      <span className="font-medium">{userAnalytics.avgEngagement || 'N/A'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-8">
              <BarChart3 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">Loading analytics data...</p>
            </div>
          )}
          
          <DialogFooter>
            <Button
              onClick={() => setShowAnalyticsDialog(false)}
              data-testid="button-close-analytics"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}